// hooks
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation } from "react-query";

// components
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import toast from "react-hot-toast";
import { Button } from "primereact/button";
import { Toolbar } from "primereact/toolbar";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";

// utils
import TimeFormatter from "../../utils/TimeFormatter";
import ActiveStatus from "../../utils/activeStatus";
import { useGetUsers } from "../../hook/user.hook";
import UserApi from "../../api/UserApi";
import { Password } from "primereact/password";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useGetRole } from "../../hook/role.hook";
import { Dropdown } from "primereact/dropdown";

const Users = ({ permissions }) => {
  const navigate = useNavigate();
  // state
  const [globalFilter, setGlobalFilter] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectItems, setSelectItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // api calling
  const { data: roleOptions } = useGetRole({ filter_super_admin: true });
  const { data: usersData, isLoading, isSuccess, isError, refetch } = useGetUsers();

  // query
  const { isLoading: createLoading, mutate: crateMutate } = useMutation(async (data) => await UserApi.Add(data), {
    onSettled: (response, error) => {
      if (response.data.status === 200) {
        refetch();
        setShowCreateModal(false);
        reset({});
        toast.success("User Created!", { duration: 4000 });
      } else {
        refetch();
        setShowCreateModal(false);
        toast.error(response.data.message, { duration: 5000 });
      }
    },
  });
  const { isLoading: deleteLoading, mutate: deleteMutate } = useMutation(async (data) => await UserApi.Delete(data), {
    onSettled: (response, error) => {
      if (response.data.status === 200) {
        setSelectItems([]);
        refetch();
        setShowDeleteModal(false);
        setShowDeleteItemModal(false);
        toast.success("User Deleted!", { duration: 4000 });
      } else {
        setSelectItems([]);
        refetch();
        setShowDeleteModal(false);
        setShowDeleteItemModal(false);
        toast.error(response.data.message, { duration: 5000 });
      }
    },
  });

  // react hook
  const { register, handleSubmit, control, formState, reset } = useForm();

  // functions
  const create = (data) => {
    const selected_role = roleOptions.find((role) => role._id === data.role_id);
    let user_level = "admin";

    if (selected_role?.name.toLowerCase() === "agent") {
      user_level = "user";
    }

    crateMutate({ ...data, user_level: user_level });
  };

  const createModalHandler = () => {
    setShowCreateModal(true);
  };

  const showDeleteItemConfirmation = (data) => {
    setSelectItems([data]);
    setShowDeleteItemModal(true);
  };

  const deleteSelectedItem = () => {
    let payload = {
      user_id: [],
    };

    for (let i = 0; i < selectItems.length; i++) {
      payload.user_id.push(selectItems[i]._id);
    }

    deleteMutate(payload);
  };

  const confirmDeleteItem = () => {
    const data = selectItems[0];

    let payload = {
      user_id: [data._id],
    };
    deleteMutate(payload);
  };

  const confirmDeleteSelected = () => {
    setShowDeleteModal(true);
  };

  // life cycle
  useEffect(() => {
    if (!isLoading && isSuccess) {
      let temp_data = [];

      for (let i = 0; i < usersData.length; i++) {
        temp_data.push({ ...usersData[i], updated_at: TimeFormatter(usersData[i].updated_at, "ll"), created_at: TimeFormatter(usersData[i].created_at, "ll") });
      }

      setUsers(temp_data);
    }
  }, [isLoading, usersData, isSuccess]);

  useEffect(() => {
    if (isError) {
      toast.error("Please check your connections", { duration: 6000 });
    }
  }, [isError]);

  // child components
  const leftToolbarTemplate = () => {
    return (
      <React.Fragment>
        <div className="my-2">
          <Button label="New" icon="pi pi-plus" className="p-button-success mr-2" onClick={createModalHandler} />
          {permissions.delete && <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={confirmDeleteSelected} disabled={!selectItems.length} />}
        </div>
      </React.Fragment>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="actions">
        {permissions.update && <Button icon="pi pi-pencil" className="p-button-rounded p-button-warning mr-2" onClick={() => navigate(`/dashboard/users/update/${rowData._id}`)} />}
        {permissions.delete && <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-2" onClick={() => showDeleteItemConfirmation(rowData)} />}
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => {
    let status = rowData.active_status ? "instock" : "outofstock";
    return <span className={`product-badge status-${status}`}>{ActiveStatus(rowData.active_status)}</span>;
  };

  const header = () => {
    return (
      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
        <h5 className="m-0">Manage users</h5>
        <span className="block mt-2 md:mt-0 p-input-icon-left">
          <i className="pi pi-search" />
          <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." />
        </span>
      </div>
    );
  };

  const deleteSingleItemFooter = () => {
    return (
      <>
        <Button label="No" icon="pi pi-times" className="p-button-text" onClick={() => setShowDeleteItemModal(false)} />
        <Button label="Yes" loading={deleteLoading} icon="pi pi-check" className="p-button-text" onClick={confirmDeleteItem} />
      </>
    );
  };

  const deleteMultipleItemFooter = () => {
    return (
      <>
        <Button label="No" icon="pi pi-times" className="p-button-text" onClick={() => setShowDeleteModal(false)} />
        <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteSelectedItem} loading={deleteLoading} />
      </>
    );
  };

  return (
    <>
      <div className="grid crud-demo ">
        <div className="col-12  mx-auto">
          <div className="card">
            <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>
            <DataTable
              loading={isLoading}
              value={users}
              selection={selectItems}
              onSelectionChange={(e) => setSelectItems(e.value)}
              dataKey="_id"
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25]}
              className="datatable-responsive"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
              globalFilter={globalFilter}
              emptyMessage="No User found."
              header={header}
              responsiveLayout="scroll"
            >
              <Column selectionMode="multiple" headerStyle={{ width: "3rem" }}></Column>
              <Column field="name" header="Name" sortable headerStyle={{ width: "auto", minWidth: "10rem" }}></Column>
              <Column field="email" header="E-Mail" sortable headerStyle={{ width: "20%", minWidth: "10rem" }}></Column>
              <Column body={statusBodyTemplate} field="active_status" header="Status" sortable headerStyle={{ width: "10%", minWidth: "5rem" }}></Column>
              <Column field="role_id.name" header="Role" sortable headerStyle={{ width: "10%", minWidth: "10rem" }}></Column>
              <Column field="updated_at" header="Updated at" sortable headerStyle={{ width: "20%", minWidth: "10rem" }}></Column>
              <Column field="created_at" header="Created at" sortable headerStyle={{ width: "20%", minWidth: "10rem" }}></Column>
              {permissions.update || permissions.delete ? <Column header="Actions" body={actionBodyTemplate} headerStyle={{ width: "10%", minWidth: "10rem" }}></Column> : null}
            </DataTable>
          </div>
        </div>
      </div>

      {/* create */}
      <Dialog visible={showCreateModal} header="Create User" modal className="p-fluid modal-container" footer onHide={() => setShowCreateModal(false)}>
        <form onSubmit={handleSubmit(create)}>
          <div className="field">
            <label htmlFor="name">Name</label>
            <InputText placeholder="User Name" {...register("name", { required: true })} id="name" type="text" className={formState.errors.name && "p-invalid"} />
            {formState.errors.name && (
              <small id="name-help" className="p-error block">
                This field is required
              </small>
            )}
          </div>

          <div className="field">
            <label htmlFor="email">Email</label>
            <InputText placeholder="Email address" {...register("email", { required: true })} id="name" type="text" className={formState.errors.email && "p-invalid"} />
            {formState.errors.email && (
              <small id="name-help" className="p-error block">
                This field is required
              </small>
            )}
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <Controller
              rules={{ required: true }}
              control={control}
              name="password"
              render={({ field }) => <Password value={field.value} autoComplete="current-password" placeholder="Input Password" onChange={(e) => field.onChange(e)} toggleMask feedback={false} className="w-full" inputClassName={classNames("w-full", { "p-invalid": formState.errors.password })} />}
            />
            {formState.errors.password && (
              <small id="name" className="p-error block pt-1">
                Input Password
              </small>
            )}
          </div>
          <div className="field ">
            <label htmlFor="choose-kurir">Role :</label>
            <Controller
              control={control}
              defaultValue={""}
              name="role_id"
              render={({ field }) => (
                <Dropdown
                  filter
                  showClear
                  ref={field.ref}
                  optionLabel="name"
                  optionValue="_id"
                  disabled={!roleOptions?.length}
                  value={field.value}
                  onBlur={field.onBlur}
                  options={roleOptions}
                  className={classNames({
                    "p-invalid": formState.errors.role_id,
                  })}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                  placeholder="Pilih Role"
                />
              )}
            />
            {formState.errors.role_id && (
              <small id="choose-kurir" className="p-error block pt-1">
                Anda belum role
              </small>
            )}
          </div>
          <div className="field">
            <label htmlFor="phone_number">Phone Number</label>
            <InputText placeholder="Ex : 08522.." {...register("phone_number", { required: true })} id="name" type="text" className={formState.errors.phone_number && "p-invalid"} />
            {formState.errors.phone_number && (
              <small id="name-help" className="p-error block">
                This field is required
              </small>
            )}
          </div>
          <div className="field">
            <label className="block" htmlFor="active_status">
              Active status
            </label>
            <Controller defaultValue={false} control={control} name="active_status" render={({ field }) => <InputSwitch onChange={(e) => field.onChange(e)} checked={field.value} />} />
          </div>
          <div className="flex justify-content-end">
            <div className="flex">
              <Button type="button" onClick={() => setShowCreateModal(false)} label="Cancel" icon="pi pi-times" className="p-button-text" />
              <Button loading={createLoading} label="Save" icon="pi pi-check" className="p-button-text" />
            </div>
          </div>
        </form>
      </Dialog>

      {/* delete single */}
      <Dialog visible={showDeleteItemModal} className="modal-container" header="Confirm" modal footer={deleteSingleItemFooter} onHide={() => setShowDeleteItemModal(false)}>
        <div className="flex">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
          {selectItems.length && (
            <span>
              Are you sure you want to delete <b>{selectItems[0]?.name}</b>?
            </span>
          )}
        </div>
      </Dialog>

      {/* delete multiple */}
      <Dialog visible={showDeleteModal} className="modal-container" header="Confirm" modal footer={deleteMultipleItemFooter} onHide={() => setShowDeleteModal(false)}>
        <div className="flex">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
          {selectItems.length && <span>Are you sure you want to delete the selected users?</span>}
        </div>
      </Dialog>
    </>
  );
};

const comparisonFn = function (prevProps, nextProps) {
  return prevProps.location?.pathname === nextProps.location?.pathname;
};

export default React.memo(Users, comparisonFn);
